<div class="zm" data-testid="zone-management-workspace">
  @if (loading) {
    <div class="zm-empty"><mat-spinner diameter="34" strokeWidth="3"></mat-spinner></div>
  } @else {
    <div class="zm-toolbar">
      <div class="zm-toolbar__group">
        <mat-icon class="zm-toolbar__lead">map</mat-icon>
        @if (floorPlans.length) {
          <select class="zm-select" [ngModel]="activeFloorPlan?.id"
                  (ngModelChange)="selectFloorPlan(floorPlanById($event))" aria-label="Floor plan">
            @for (fp of floorPlans; track fp.id) {
              <option [value]="fp.id">{{ fp.name }} — {{ mappedZoneCount(fp.id) }} zones</option>
            }
          </select>
        } @else {
          <span class="zm-toolbar__title">Zones</span>
        }
        <button class="zm-iconbtn" title="Add floor plan" (click)="addFloorPlan()" [disabled]="viewOnly"><mat-icon>add</mat-icon></button>
        @if (activeFloorPlan) {
          <button class="zm-iconbtn" title="Edit floor plan" (click)="editFloorPlan()" [disabled]="viewOnly"><mat-icon>edit</mat-icon></button>
          <button class="zm-iconbtn zm-iconbtn--danger" title="Delete floor plan" (click)="deleteFloorPlan()" [disabled]="viewOnly"><mat-icon>delete_outline</mat-icon></button>
        }
      </div>

      @if (activeFloorPlan && !viewOnly) {
        <div class="zm-toolbar__group zm-tools" aria-label="Zone drawing tools">
          <button class="zm-tool" [class.is-active]="tool === 'select'" (click)="setTool('select')" title="Select and move"><mat-icon>near_me</mat-icon></button>
          <button class="zm-tool" [class.is-active]="tool === 'rect'" (click)="setTool('rect')" title="Draw zone rectangle"><mat-icon>crop_square</mat-icon></button>
          <button class="zm-tool" [class.is-active]="tool === 'polygon'" (click)="setTool('polygon')" title="Draw zone polygon"><mat-icon>pentagon</mat-icon></button>
          <button class="zm-tool" [class.is-active]="tool === 'pin'" (click)="setTool('pin')" title="Place zone pin"><mat-icon>place</mat-icon></button>
        </div>

        <div class="zm-toolbar__group zm-tools" aria-label="Floor plan drawing tools">
          <button class="zm-tool" [class.is-active]="editingPlanElements" (click)="togglePlanEditing()" title="Edit plan lines and labels"><mat-icon>architecture</mat-icon></button>
          <button class="zm-tool" [class.is-active]="tool === 'wall'" (click)="setTool('wall')" title="Draw wall or line"><mat-icon>timeline</mat-icon></button>
          <button class="zm-tool" [class.is-active]="tool === 'label'" (click)="setTool('label')" title="Add label"><mat-icon>text_fields</mat-icon></button>
          @if (selectedPlanElementId) {
            <button class="zm-tool" (click)="removeSelectedPlanElement()" title="Remove selected plan element"><mat-icon>delete_outline</mat-icon></button>
          }
        </div>

        <div class="zm-toolbar__group">
          <app-color-picker [color]="color" (colorChange)="onColorChange($event)"></app-color-picker>
        </div>
      }

      <div class="zm-toolbar__spacer"></div>

      <div class="zm-toolbar__group">
        @if (activeFloorPlan) {
          <button class="zm-iconbtn" title="Show or hide zone names" (click)="toggleLabels()"><mat-icon>{{ showLabels ? 'label' : 'label_off' }}</mat-icon></button>
          <button class="zm-iconbtn" title="Fit plan to view" (click)="resetView()"><mat-icon>fit_screen</mat-icon></button>
        }
        @if (canSeeDutchie && !viewOnly) {
          <button class="zm-btn" (click)="syncDutchie()" [disabled]="dirty || refreshingZones">
            @if (refreshingZones) { <mat-spinner diameter="18" strokeWidth="3"></mat-spinner> }
            <span>Sync Dutchie</span>
          </button>
        }
        @if (!viewOnly) {
          <button class="zm-btn" (click)="revert()" [disabled]="!dirty"><mat-icon>undo</mat-icon><span>Undo current edits</span></button>
          @if (conflicted) {
            <button class="zm-btn" (click)="reloadAfterConflict()"><mat-icon>refresh</mat-icon><span>Reload</span></button>
          }
          <button class="zm-btn zm-btn--primary" (click)="save()" [disabled]="saving || !dirty || conflicted || creatingZone">
            @if (saving) { <mat-spinner diameter="18" strokeWidth="3"></mat-spinner> } @else { <mat-icon>save</mat-icon> }
            <span>Save changes</span>
          </button>
        }
      </div>
    </div>

    @if (viewOnly) {
      <div class="zm-mobile-note"><mat-icon>info</mat-icon> Zone editing requires a tablet or desktop. This view is read-only.</div>
    }

    <div class="zm-body">
      <aside class="zm-panel zm-panel--tree">
        <div class="zm-panel__heading">
          <span class="zm-panel__label">Zone hierarchy</span>
          @if (!viewOnly) {
            <button class="zm-mini-btn" (click)="configureLevels()" title="Configure zone levels"><mat-icon>settings</mat-icon></button>
          }
        </div>

        @if (dbZone.zoneTitles.length) {
          <div class="zm-levels">
            @for (title of dbZone.zoneTitles; track $index) {
              <button class="zm-level" [class.is-off]="levelVisible[$index] === false" (click)="toggleLevel($index)">
                <mat-icon>{{ levelVisible[$index] === false ? 'visibility_off' : 'visibility' }}</mat-icon>
                {{ title }}
              </button>
            }
          </div>
        } @else {
          <div class="zm-tree__empty">Configure zone levels to begin.</div>
        }

        @if (focusZoneName) {
          <button class="zm-crumb" (click)="exitZone()"><mat-icon>arrow_back</mat-icon> {{ focusZoneName }}</button>
        }

        <input class="zm-search" [(ngModel)]="zoneSearch" placeholder="Search zones" aria-label="Search zones">

        <div class="zm-tree">
          @for (row of zoneRows; track row.zone.id) {
            <div class="zm-row" [class.is-selected]="selectedZoneId === row.zone.id"
                 [class.is-drop-target]="canDropZoneOn(row.zone.id)"
                 [style.paddingLeft.px]="10 + row.depth * 16" (click)="rowClick(row)"
                 [draggable]="!viewOnly" (dragstart)="startZoneDrag(row.zone.id, $event)"
                 (dragover)="canDropZoneOn(row.zone.id) && $event.preventDefault()"
                 (drop)="dropZoneOn(row.zone.id, $event)" (dragend)="endZoneDrag()">
              <span class="zm-row__dot" [style.background]="isMapped(row.zone) ? (row.zone.mapData?.color || '#2f9e54') : 'transparent'"
                    [class.zm-row__dot--empty]="!isMapped(row.zone)"></span>
              <span class="zm-row__name">{{ row.zone.name }}</span>
              @if (row.zone.mapData?.shape) { <mat-icon class="zm-row__mapped" title="Mapped">check_circle</mat-icon> }
              @if (row.zone.zones?.length) {
                <button class="zm-row__action" title="Focus children" (click)="$event.stopPropagation(); enterZone(row.zone.id)"><mat-icon>login</mat-icon></button>
              }
              @if (!viewOnly && row.depth < dbZone.zoneTitles.length - 1) {
                <button class="zm-row__action" title="Add child zone" (click)="$event.stopPropagation(); startCreateZone(row.zone.id)"><mat-icon>add</mat-icon></button>
              }
            </div>
          }
          @if (zoneRows.length === 0 && dbZone.zoneTitles.length) {
            <p class="zm-tree__empty">No zones found.</p>
          }
        </div>

        @if (!viewOnly && dbZone.zoneTitles.length) {
          <button class="zm-btn zm-btn--wide" (click)="startCreateZone(null)"><mat-icon>add</mat-icon> Add root zone</button>
        }
      </aside>

      <div class="zm-canvas">
        @if (activeFloorPlan) {
          <div #stageHost class="zm-stage"></div>

          @if (!viewOnly && tool !== 'select' && !selectedZoneId && !creatingZone && tool !== 'wall' && tool !== 'label') {
            <div class="zm-hint"><mat-icon>add_location_alt</mat-icon> Draw to create a new zone, or select an existing zone first.</div>
          } @else if (tool === 'polygon') {
            <div class="zm-hint">
              <mat-icon>pentagon</mat-icon> Click to add points.
              <button class="zm-hint__btn" (click)="finishPolygon()">Finish</button>
              <button class="zm-hint__btn zm-hint__btn--ghost" (click)="cancelPolygon()">Cancel</button>
            </div>
          } @else if (tool === 'wall') {
            <div class="zm-hint">
              <mat-icon>timeline</mat-icon> Click to add connected wall points.
              <button class="zm-hint__btn" (click)="finishWall()">Finish</button>
              <button class="zm-hint__btn zm-hint__btn--ghost" (click)="cancelWall()">Cancel</button>
            </div>
          } @else if (tool === 'rect') {
            <div class="zm-hint"><mat-icon>crop_square</mat-icon> Drag to draw a zone rectangle.</div>
          } @else if (tool === 'pin') {
            <div class="zm-hint"><mat-icon>place</mat-icon> Click to place a zone pin.</div>
          } @else if (tool === 'label') {
            <div class="zm-hint"><mat-icon>text_fields</mat-icon> Click where the label should appear.</div>
          }
        } @else {
          <div class="zm-empty">
            <div class="zm-empty__card">
              <div class="zm-empty__icon"><mat-icon>map</mat-icon></div>
              <h2>Add a floor plan when you're ready</h2>
              <p>Upload an image or start with a blank grid. Mapping is optional; zones can still be managed from the hierarchy.</p>
              @if (!viewOnly) {
                <button class="zm-btn zm-btn--primary" (click)="addFloorPlan()"><mat-icon>add</mat-icon> Add floor plan</button>
              }
            </div>
          </div>
        }
      </div>

      <aside class="zm-panel zm-panel--inspector">
        @if (creatingZone) {
          <span class="zm-panel__label">Create zone</span>
          <label class="zm-field"><span>Name</span><input [(ngModel)]="newZoneName" autofocus></label>
          <label class="zm-field">
            <span>Parent</span>
            <select [(ngModel)]="newZoneParentId">
              <option [ngValue]="null">Root ({{ dbZone.zoneTitles[0] }})</option>
              @for (zone of createParentOptions; track zone.id) { <option [value]="zone.id">{{ zone.name }}</option> }
            </select>
          </label>
          <label class="zm-field"><span>Placement capacity</span><input type="number" min="0" [(ngModel)]="newZonePlacements"></label>
          @if (pendingShape) { <div class="zm-status"><mat-icon>check_circle</mat-icon> Shape ready on {{ activeFloorPlan?.name }}</div> }
          <div class="zm-inspector-actions">
            <button class="zm-btn" (click)="cancelCreateZone()">Cancel</button>
            <button class="zm-btn zm-btn--primary" (click)="createZone()">Create zone</button>
          </div>
        } @else if (selectedZone) {
          <span class="zm-panel__label">Zone details</span>
          <label class="zm-field"><span>Name</span><input [(ngModel)]="selectedZone.name" [disabled]="viewOnly"></label>
          @if (selectedParentOptions.length) {
            <label class="zm-field">
              <span>Parent</span>
              <select [ngModel]="selectedParentId" (ngModelChange)="moveSelectedZone($event)" [disabled]="viewOnly">
                @for (zone of selectedParentOptions; track zone.id) { <option [value]="zone.id">{{ zone.name }}</option> }
              </select>
            </label>
          }
          <label class="zm-field">
            <span>Placement capacity</span>
            <input type="number" min="0" [(ngModel)]="selectedZone.placements" [disabled]="viewOnly || !selectedCanEditPlacements">
          </label>
          <div class="zm-status" [class.is-muted]="!selectedZone.mapData?.shape">
            <mat-icon>{{ selectedZone.mapData?.shape ? 'place' : 'location_off' }}</mat-icon>
            {{ selectedZone.mapData?.shape ? 'Mapped on ' + floorPlanById(selectedZone.mapData.floorPlanId)?.name : 'Not mapped' }}
          </div>
          @if (selectedSensors.length) {
            <div class="zm-linked-list"><strong>Sensors</strong>@for (sensor of selectedSensors; track sensor.id) { <span>{{ sensor.name }}</span> }</div>
          }
          @if (!viewOnly) {
            @if (canAddChildSelected) {
              <button class="zm-btn zm-btn--wide" (click)="startCreateZone(selectedZone.id)"><mat-icon>add</mat-icon> Add child zone</button>
            }
            @if (selectedZone.mapData?.shape) {
              <button class="zm-btn zm-btn--wide" (click)="clearSelectedShape()"><mat-icon>location_off</mat-icon> Remove map shape</button>
            }
            <button class="zm-btn zm-btn--wide" (click)="copySelectedZone()"><mat-icon>content_copy</mat-icon> Copy zone</button>
            <button class="zm-btn zm-btn--wide zm-btn--danger" (click)="deleteSelectedZone()"><mat-icon>delete_outline</mat-icon> Delete zone</button>
          }
        } @else {
          <span class="zm-panel__label">Zone details</span>
          <div class="zm-inspector-empty"><mat-icon>touch_app</mat-icon><p>Select a zone to edit its details, or draw on the map to create one.</p></div>
        }
      </aside>
    </div>
  }
</div>
